/**
 * @file index.tsx
 * @description This is the main entry point for the React application.
 * It initializes authentication (MSAL), internationalization (i18next), Fluent UI icons,
 * and sets up the main routing structure for the entire app.
 */

import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { I18nextProvider } from "react-i18next";
import { HelmetProvider } from "react-helmet-async";
import { initializeIcons } from "@fluentui/react";
import { MsalProvider } from "@azure/msal-react";

import "./index.css";

import Chat from "./pages/chat/Chat";
import Layout from "./pages/layout/Layout";
import NoPage from "./pages/NoPage";
import Ask from "./pages/ask/Ask";
import { Profile } from "./pages/profile/Profile";
import Welcome from "./pages/welcome/Welcome";
import { AuthProvider, StaticAuthProvider, useAuth } from "./auth/authProvider";
import { Login } from "./pages/login/Login";
import { useLogin } from "./authConfig";
import { useTranslation } from "react-i18next"; // Import useTranslation
import i18n from "./i18n";
import { Redirect } from "./pages/redirect/Redirect";
import { initializeMsal } from "./auth/msalBootstrap";

// Initialize Fluent UI icons for the application
initializeIcons();

/**
 * @component AppRoutes
 * @description A component that defines the application's routing logic based on authentication status.
 * It handles loading states and redirects unauthenticated users to the login page when authentication is enabled.
 * @returns {JSX.Element} The appropriate routes or a loading/login component.
 */
const AppRoutes = () => {
    // Hooks to determine authentication status and whether login is enabled.
    const { t } = useTranslation(); // Access translation function
    const { isAuthenticated, isLoading } = useAuth();
    const loginEnabled = Boolean(useLogin());

    console.log(`[AppRoutes] Rendering. loginEnabled: ${loginEnabled}, isLoading: ${isLoading}, isAuthenticated: ${isAuthenticated}`);

    // If login is enabled and authentication is in progress, show a loading message.
    if (loginEnabled && isLoading) return <div>{t("auth.loading")}</div>;
    // If login is enabled and the user is not authenticated, render the Login component.
    if (loginEnabled && !isAuthenticated) return <Login />;

    // Once authenticated (or if login is disabled), render the main application routes.
    return (
        <Routes>
            {/* The Layout component provides the consistent header and navigation for all pages */}
            <Route element={<Layout />}>
                {/* Define the routes for different pages within the application */}
                <Route path="/" element={<Welcome />} />
                <Route path="/chat" element={<Chat />} />
                <Route path="/ask" element={<Ask />} />
                <Route path="/profile" element={<Profile />} />
                {/* A catch-all route for any undefined paths, leading to a 404 page */}
                <Route path="*" element={<NoPage />} />
            </Route>
        </Routes>
    );
};

async function startApp() {
    /**
     * @function startApp
     * @description Asynchronously initializes and renders the entire React application.
     * It sets up all the necessary providers (MSAL, Auth, i18n, etc.) before rendering the main components.
     */
    console.log("[index.tsx] Starting app...");
    const disableAuth = import.meta.env.VITE_DISABLE_AUTH === "true";

    if (!disableAuth) {
        // Display an initial message while authentication is being set up.
        document.getElementById("root")!.innerHTML = `<div>${i18n.t("app.initializingAuth")}</div>`;
    }

    // Asynchronously initialize the MSAL instance to handle authentication.
    const initializedMsalInstance = disableAuth ? null : await initializeMsal();

    // Render the main application into the root DOM element.
    ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
        <React.StrictMode>
            {/* Provides internationalization capabilities to the entire app */}
            <I18nextProvider i18n={i18n}>
                {/* Allows for dynamic management of the document head (e.g., page titles) */}
                <HelmetProvider>
                    {/* Provides the MSAL instance to all child components for authentication */}
                    {initializedMsalInstance ? (
                        <MsalProvider instance={initializedMsalInstance}>
                            {/* Custom authentication provider that wraps MSAL logic */}
                            <AuthProvider>
                                {/* Enables client-side routing */}
                                <BrowserRouter>
                                    <Routes>
                                        {/* A dedicated route for handling MSAL redirect callbacks */}
                                        <Route path="/redirect" element={<Redirect />} />
                                        {/* The main application routes, handled by the AppRoutes component */}
                                        <Route path="*" element={<AppRoutes />} />
                                    </Routes>
                                </BrowserRouter>
                            </AuthProvider>
                        </MsalProvider>
                    ) : (
                        <StaticAuthProvider>
                            {/* Enables client-side routing */}
                            <BrowserRouter>
                                <Routes>
                                    {/* No redirect route needed when auth is disabled */}
                                    <Route path="*" element={<AppRoutes />} />
                                </Routes>
                            </BrowserRouter>
                        </StaticAuthProvider>
                    )}
                </HelmetProvider>
            </I18nextProvider>
        </React.StrictMode>
    );
}

// Start the application and log any startup errors.
startApp().catch(err => console.error("[index.tsx] App startup error:", err));
